

// Simple reusable Lightbox module
export default class Lightbox {
  constructor(options = {}) {
    const {
      triggerSelector = 'a[data-lightbox]',
      modalId = 'lightbox',
      template = null,
      attribute = 'href',
      closeOnEscape = true,
      closeOnBackdrop = true,
      closeSelector = '.lightbox__close',
      groupAttribute = null,
    } = options;

    this.triggerSelector = triggerSelector;
    this.modalId = modalId;
    this.attribute = attribute;
    this.closeOnEscape = closeOnEscape;
    this.closeOnBackdrop = closeOnBackdrop;
    this.closeSelector = closeSelector;
    this.groupAttribute = groupAttribute;
    this.groups = new Map();
    this.currentGroup = null;
    this.currentIndex = -1;

    this.triggers = Array.from(document.querySelectorAll(this.triggerSelector));
    if (!this.triggers.length) return; // nothing to do

    this._render(template);
    this._bind();
    if (this.groupAttribute) this._buildGroups();
  }

  _buildGroups() {
    this.groups.clear();
    this.triggers.forEach((t) => {
      const key = t.getAttribute(this.groupAttribute);
      const groupKey = key !== null ? key : '__nogroup';
      if (!this.groups.has(groupKey)) this.groups.set(groupKey, []);
      this.groups.get(groupKey).push(t);
    });
  }

  _render(template) {
    // If an element with modalId exists, reuse it
    this.modal = document.getElementById(this.modalId);
    if (!this.modal) {
      this.modal = document.createElement('div');
      this.modal.id = this.modalId;
      this.modal.classList.add('lightbox');
      // also add a modifier class derived from the id so CSS can target specific instances
      this.modal.classList.add(`lightbox--${this.modalId}`);
      this.modal.innerHTML = template || `<div class="lightbox__inner"><button class="lightbox__close" aria-label="Cerrar">×</button><img class="lightbox__img" src="" alt=""/></div>`;
      document.body.appendChild(this.modal);
    }
    this.img = this.modal.querySelector('.lightbox__img') || this.modal.querySelector('img');
    // Prefer explicit selector, fallback to data attribute
    this.closeBtn = this.modal.querySelector(this.closeSelector) || this.modal.querySelector('[data-lightbox-close]');
    // Prev / Next buttons (BEM naming expected)
    this.prevBtn = this.modal.querySelector('.lightbox__prev');
    this.nextBtn = this.modal.querySelector('.lightbox__next');
    const inner = this.modal.querySelector('.lightbox__inner') || this.modal.firstElementChild;
    if (!this.prevBtn && inner) {
      this.prevBtn = document.createElement('button');
      this.prevBtn.className = 'lightbox__prev';
      this.prevBtn.setAttribute('aria-label', 'Previous');
      this.prevBtn.innerHTML = '‹';
      inner.appendChild(this.prevBtn);
    }
    if (!this.nextBtn && inner) {
      this.nextBtn = document.createElement('button');
      this.nextBtn.className = 'lightbox__next';
      this.nextBtn.setAttribute('aria-label', 'Next');
      this.nextBtn.innerHTML = '›';
      inner.appendChild(this.nextBtn);
    }
  }

  _bind() {
    this._onTriggerClick = this._onTriggerClick.bind(this);
    this._onModalClick = this._onModalClick.bind(this);
    this._onKeyDown = this._onKeyDown.bind(this);

    this.triggers.forEach(t => t.addEventListener('click', this._onTriggerClick));
    this.modal.addEventListener('click', this._onModalClick);
    if (this.closeOnEscape) document.addEventListener('keydown', this._onKeyDown);
    // prev/next handlers
    this._onPrevClick = this._onPrevClick.bind(this);
    this._onNextClick = this._onNextClick.bind(this);
    if (this.prevBtn) this.prevBtn.addEventListener('click', this._onPrevClick);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this._onNextClick);
  }

  _onTriggerClick(e) {
    e.preventDefault();
    const el = e.currentTarget;
    const src = el.getAttribute(this.attribute) || el.href;
    const alt = el.getAttribute('data-alt') || el.getAttribute('title') || '';
    this.open(src, { alt, trigger: el });
  }

  _onModalClick(e) {
    // Close when the close control is clicked
    if (e.target === this.closeBtn) {
      this.close();
      return;
    }
    // Close when clicking the backdrop only if allowed
    if (e.target === this.modal && this.closeOnBackdrop) {
      this.close();
    }
  }

  _onKeyDown(e) {
    if (e.key === 'Escape') this.close();
    if (e.key === 'ArrowRight') this.next();
    if (e.key === 'ArrowLeft') this.prev();
  }

  open(src, { alt = '', trigger = null } = {}) {
    if (!this.modal) return;
    if (trigger && this.groupAttribute) {
      const key = trigger.getAttribute(this.groupAttribute);
      const groupKey = key !== null ? key : '__nogroup';
      const group = this.groups.get(groupKey) || [];
      this.currentGroup = groupKey;
      this.currentIndex = group.indexOf(trigger);
      // normalize
      if (this.currentIndex === -1) this.currentIndex = 0;
    }
    this.img.src = src || '';
    if (alt) this.img.alt = alt;
    this.modal.classList.add('open');
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('open');
    if (this.img) this.img.src = '';
  }

  destroy() {
    this.triggers.forEach(t => t.removeEventListener('click', this._onTriggerClick));
    this.modal.removeEventListener('click', this._onModalClick);
    document.removeEventListener('keydown', this._onKeyDown);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this._onPrevClick);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this._onNextClick);
    if (this.modal && this.modal.parentNode) this.modal.parentNode.removeChild(this.modal);
    this.modal = null;
  }

  next() {
    if (!this.groupAttribute || !this.currentGroup) return;
    const group = this.groups.get(this.currentGroup) || [];
    if (!group.length) return;
    this.currentIndex = (this.currentIndex + 1) % group.length;
    const nextTrigger = group[this.currentIndex];
    const src = nextTrigger.getAttribute(this.attribute) || nextTrigger.href;
    const alt = nextTrigger.getAttribute('data-alt') || nextTrigger.getAttribute('title') || '';
    this.open(src, { alt, trigger: nextTrigger });
  }

  prev() {
    if (!this.groupAttribute || !this.currentGroup) return;
    const group = this.groups.get(this.currentGroup) || [];
    if (!group.length) return;
    this.currentIndex = (this.currentIndex - 1 + group.length) % group.length;
    const prevTrigger = group[this.currentIndex];
    const src = prevTrigger.getAttribute(this.attribute) || prevTrigger.href;
    const alt = prevTrigger.getAttribute('data-alt') || prevTrigger.getAttribute('title') || '';
    this.open(src, { alt, trigger: prevTrigger });
  }

  _onPrevClick(e) {
    e.stopPropagation();
    this.prev();
  }

  _onNextClick(e) {
    e.stopPropagation();
    this.next();
  }
}
